import { logAdminAction } from "./auditLog";
import { supabase } from "./supabase";

export type PositionStatus = "draft" | "active" | "filled" | "closed";
export type EmploymentType = "full_time" | "part_time" | "internship" | "contract";

export type Position = {
  id: string;
  company_id: string;
  title: string;
  description: string | null;
  location: string | null;
  employment_type: EmploymentType | null;
  comp_band: string | null;
  grad_year_min: number | null;
  grad_year_max: number | null;
  eligible_majors: string[];
  eligible_universities: string[];
  us_citizen_required: boolean;
  gpa_min: number | null;
  status: PositionStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

export type PositionInput = {
  company_id: string;
  title: string;
  description?: string | null;
  location?: string | null;
  employment_type?: EmploymentType | null;
  comp_band?: string | null;
  grad_year_min?: number | null;
  grad_year_max?: number | null;
  eligible_majors?: string[];
  eligible_universities?: string[];
  us_citizen_required?: boolean;
  gpa_min?: number | null;
  status?: PositionStatus;
};

export type PositionWithInterestCount = Position & { active_interest_count: number };

/**
 * List all positions linked to a competition, hydrated with
 * `active_interest_count`. The interest count is filtered to non-withdrawn
 * rows; the list page renders this as "{N} students opted in" per card.
 *
 * Counts are computed client-side via a parallel query — there's no read
 * RLS path that lets a non-admin client see other students' interests, but
 * the active count is non-PII (it's the same number that would surface
 * once judging closes), so we expose it. The actual student identities
 * are still gated behind the post-judging RPC (Prompt 2).
 */
export async function listPositionsForCompetition(
  competitionId: string
): Promise<PositionWithInterestCount[]> {
  // Step 1: linked positions for this comp.
  const { data: linkRows } = await supabase
    .from("competition_positions")
    .select("position_id")
    .eq("competition_id", competitionId);
  const positionIds = (linkRows ?? []).map((r) => r.position_id as string);
  if (positionIds.length === 0) return [];

  const { data: posRows, error: posErr } = await supabase
    .from("positions")
    .select("*")
    .in("id", positionIds);
  if (posErr) throw new Error(posErr.message);

  // Step 2: counts. We can't COUNT through RLS as a client (interests are
  // private to students + admins), so we go through a SECURITY DEFINER RPC
  // that returns just the aggregate. See `position_interest_counts`.
  const counts = await getPositionInterestCounts(competitionId);
  const out: PositionWithInterestCount[] = (posRows ?? []).map((p) => ({
    ...(p as Position),
    active_interest_count: counts.get(p.id as string) ?? 0,
  }));
  // Sort: active first, then by title.
  out.sort((a, b) => {
    if (a.status !== b.status) {
      const order = { active: 0, draft: 1, filled: 2, closed: 3 } as Record<PositionStatus, number>;
      return order[a.status] - order[b.status];
    }
    return a.title.localeCompare(b.title);
  });
  return out;
}

export async function listAllPositionsForCompany(companyId: string): Promise<Position[]> {
  const { data, error } = await supabase
    .from("positions")
    .select("*")
    .eq("company_id", companyId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as Position[];
}

export async function createPosition(input: PositionInput): Promise<{ id: string | null; error: Error | null }> {
  const { data, error } = await supabase
    .from("positions")
    .insert({
      company_id: input.company_id,
      title: input.title,
      description: input.description ?? null,
      location: input.location ?? null,
      employment_type: input.employment_type ?? null,
      comp_band: input.comp_band ?? null,
      grad_year_min: input.grad_year_min ?? null,
      grad_year_max: input.grad_year_max ?? null,
      eligible_majors: input.eligible_majors ?? [],
      eligible_universities: input.eligible_universities ?? [],
      us_citizen_required: input.us_citizen_required ?? false,
      gpa_min: input.gpa_min ?? null,
      status: input.status ?? "active",
    })
    .select("id")
    .single();
  if (error || !data) return { id: null, error: new Error(error?.message ?? "Could not create position") };
  await logAdminAction({
    action: "position.create",
    entityType: "positions",
    entityId: data.id as string,
    summary: `Created position: ${input.title}`,
    diff: {
      before: null,
      after: {
        title: input.title,
        company_id: input.company_id,
        status: input.status ?? "active",
      },
    },
  });
  return { id: data.id as string, error: null };
}

export async function updatePosition(
  id: string,
  patch: Partial<PositionInput>
): Promise<{ error: Error | null }> {
  const update: Record<string, unknown> = {};
  if (patch.title !== undefined) update.title = patch.title;
  if (patch.description !== undefined) update.description = patch.description;
  if (patch.location !== undefined) update.location = patch.location;
  if (patch.employment_type !== undefined) update.employment_type = patch.employment_type;
  if (patch.comp_band !== undefined) update.comp_band = patch.comp_band;
  if (patch.grad_year_min !== undefined) update.grad_year_min = patch.grad_year_min;
  if (patch.grad_year_max !== undefined) update.grad_year_max = patch.grad_year_max;
  if (patch.eligible_majors !== undefined) update.eligible_majors = patch.eligible_majors;
  if (patch.eligible_universities !== undefined) update.eligible_universities = patch.eligible_universities;
  if (patch.us_citizen_required !== undefined) update.us_citizen_required = patch.us_citizen_required;
  if (patch.gpa_min !== undefined) update.gpa_min = patch.gpa_min;
  if (patch.status !== undefined) update.status = patch.status;
  update.updated_at = new Date().toISOString();
  const { error } = await supabase.from("positions").update(update).eq("id", id);
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "position.update",
    entityType: "positions",
    entityId: id,
    summary: patch.title ? `Updated position: ${patch.title}` : `Updated position`,
    diff: { after: update },
  });
  return { error: null };
}

export async function linkPositionToCompetition(
  positionId: string,
  competitionId: string
): Promise<{ error: Error | null }> {
  // The PK is (competition_id, position_id) so re-linking the same pair is
  // a no-op via upsert. We still surface a clear error if the RLS path
  // refuses (e.g. wrong company).
  const { error } = await supabase
    .from("competition_positions")
    .upsert(
      { competition_id: competitionId, position_id: positionId },
      { onConflict: "competition_id,position_id" }
    );
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "position.link",
    entityType: "competition_positions",
    entityId: positionId,
    summary: `Linked position to competition`,
    diff: { after: { competition_id: competitionId, position_id: positionId } },
  });
  return { error: null };
}

export async function unlinkPositionFromCompetition(
  positionId: string,
  competitionId: string
): Promise<{ error: Error | null }> {
  const { error } = await supabase
    .from("competition_positions")
    .delete()
    .eq("position_id", positionId)
    .eq("competition_id", competitionId);
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "position.unlink",
    entityType: "competition_positions",
    entityId: positionId,
    summary: `Unlinked position from competition`,
    diff: { before: { competition_id: competitionId, position_id: positionId }, after: null },
  });
  return { error: null };
}

/** Sets status='closed'. Does NOT delete the row — historical interests
 * remain queryable, and competition_positions linkages stay intact so the
 * audit trail survives. Use unlinkPositionFromCompetition() to remove a
 * position from a specific competition without archiving the position. */
export async function archivePosition(id: string): Promise<{ error: Error | null }> {
  // Set status='closed' directly rather than going through updatePosition,
  // so the audit row records 'position.archive' instead of a generic
  // 'position.update'. The archive event is significant enough that
  // distinguishing it in the audit log is worth the duplicated DB call.
  const { error } = await supabase
    .from("positions")
    .update({ status: "closed", updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) return { error: new Error(error.message) };
  await logAdminAction({
    action: "position.archive",
    entityType: "positions",
    entityId: id,
    summary: `Archived position`,
    diff: { after: { status: "closed" } },
  });
  return { error: null };
}

/**
 * Per-position active interest counts for a competition. Uses an RPC
 * because the interests table is gated behind a strict privacy boundary
 * (students + admins only — companies cannot see who opted in until after
 * judging). The RPC exposes ONLY the aggregate count, never the student
 * identities, so it's safe to expose to the company that owns the
 * competition. Falls back to an empty Map on error.
 */
export async function getPositionInterestCounts(
  competitionId: string
): Promise<Map<string, number>> {
  const { data, error } = await supabase.rpc("position_interest_counts", {
    p_competition_id: competitionId,
  });
  if (error || !data) return new Map();
  const out = new Map<string, number>();
  for (const row of data as { position_id: string; active_count: number }[]) {
    out.set(row.position_id, Number(row.active_count) || 0);
  }
  return out;
}

// ─── Student-side helpers ──────────────────────────────────────────────

export type CompetitionPosition = Position & {
  /** Whether the current viewer (student) has opted in for this position
   * for the active competition. */
  opted_in: boolean;
};

/**
 * Active linked positions for a comp the student is registered for, plus
 * the student's own opt-in state. Used by the registration step + the
 * dashboard's "Career interests" page.
 */
export async function listPositionsForCompetitionAsStudent(
  competitionId: string
): Promise<CompetitionPosition[]> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return [];

  const { data: linkRows } = await supabase
    .from("competition_positions")
    .select("position_id")
    .eq("competition_id", competitionId);
  const ids = (linkRows ?? []).map((r) => r.position_id as string);
  if (ids.length === 0) return [];

  const [{ data: posRows }, { data: interestRows }] = await Promise.all([
    supabase.from("positions").select("*").in("id", ids).eq("status", "active"),
    supabase
      .from("student_position_interests")
      .select("position_id, withdrawn_at")
      .eq("competition_id", competitionId)
      .eq("student_id", uid),
  ]);

  const optedIn = new Set<string>();
  for (const row of (interestRows ?? []) as { position_id: string; withdrawn_at: string | null }[]) {
    if (!row.withdrawn_at) optedIn.add(row.position_id);
  }
  return ((posRows ?? []) as Position[]).map((p) => ({ ...p, opted_in: optedIn.has(p.id) }));
}

export type PositionInterestUpsert = {
  positionId: string;
  competitionId: string;
  optedIn: boolean;
};

/**
 * Persist the student's opt-in choices. For each position:
 *   optedIn=true → upsert with withdrawn_at=null and refresh opted_in_at
 *   optedIn=false → if a row exists, set withdrawn_at=now (preserves the
 *                    history of having seen the role); if not, no-op
 *
 * This is idempotent — re-submitting the same set is safe.
 */
export async function upsertStudentPositionInterests(
  rows: PositionInterestUpsert[]
): Promise<{ error: Error | null }> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return { error: new Error("Not signed in") };
  if (rows.length === 0) return { error: null };

  const nowIso = new Date().toISOString();
  const optInRows = rows.filter((r) => r.optedIn);
  const optOutRows = rows.filter((r) => !r.optedIn);

  if (optInRows.length > 0) {
    const { error } = await supabase
      .from("student_position_interests")
      .upsert(
        optInRows.map((r) => ({
          student_id: uid,
          position_id: r.positionId,
          competition_id: r.competitionId,
          opted_in_at: nowIso,
          withdrawn_at: null,
        })),
        { onConflict: "student_id,position_id,competition_id" }
      );
    if (error) return { error: new Error(error.message) };
  }

  if (optOutRows.length > 0) {
    // Mark previously-opted-in rows as withdrawn. We do this with an
    // UPDATE on the existing PK (no-op if the row doesn't exist) rather
    // than upserting a brand-new "withdrawn" row, because an opt-out
    // without a prior opt-in is meaningless data.
    for (const r of optOutRows) {
      const { error } = await supabase
        .from("student_position_interests")
        .update({ withdrawn_at: nowIso })
        .eq("student_id", uid)
        .eq("position_id", r.positionId)
        .eq("competition_id", r.competitionId)
        .is("withdrawn_at", null);
      if (error) return { error: new Error(error.message) };
    }
  }

  return { error: null };
}

// ─── Career-interests dashboard ────────────────────────────────────────

export type StudentCareerInterest = {
  position: Position;
  competition: { id: string; name: string; status: string; company_name: string | null };
  opted_in_at: string;
  withdrawn_at: string | null;
};

export async function listMyCareerInterests(): Promise<StudentCareerInterest[]> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return [];

  const { data: rows } = await supabase
    .from("student_position_interests")
    .select("position_id, competition_id, opted_in_at, withdrawn_at")
    .eq("student_id", uid)
    .is("withdrawn_at", null);

  const interestRows = (rows ?? []) as {
    position_id: string;
    competition_id: string;
    opted_in_at: string;
    withdrawn_at: string | null;
  }[];
  if (interestRows.length === 0) return [];

  const positionIds = Array.from(new Set(interestRows.map((r) => r.position_id)));
  const competitionIds = Array.from(new Set(interestRows.map((r) => r.competition_id)));

  const [{ data: posRows }, { data: compRows }] = await Promise.all([
    supabase.from("positions").select("*").in("id", positionIds),
    supabase
      .from("competitions")
      .select("id, name, status, companies(name)")
      .in("id", competitionIds),
  ]);

  const positions = new Map((posRows ?? []).map((p) => [p.id as string, p as Position]));
  const competitions = new Map(
    ((compRows ?? []) as {
      id: string;
      name: string;
      status: string;
      companies: { name: string } | { name: string }[] | null;
    }[]).map((c) => {
      const company = Array.isArray(c.companies) ? c.companies[0] ?? null : c.companies;
      return [c.id, { id: c.id, name: c.name, status: c.status, company_name: company?.name ?? null }];
    })
  );

  const out: StudentCareerInterest[] = [];
  for (const r of interestRows) {
    const position = positions.get(r.position_id);
    const competition = competitions.get(r.competition_id);
    if (!position || !competition) continue;
    out.push({
      position,
      competition,
      opted_in_at: r.opted_in_at,
      withdrawn_at: r.withdrawn_at,
    });
  }
  return out;
}

export async function withdrawCareerInterest(
  positionId: string,
  competitionId: string
): Promise<{ error: Error | null }> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData?.user?.id ?? null;
  if (!uid) return { error: new Error("Not signed in") };
  const { error } = await supabase
    .from("student_position_interests")
    .update({ withdrawn_at: new Date().toISOString() })
    .eq("student_id", uid)
    .eq("position_id", positionId)
    .eq("competition_id", competitionId)
    .is("withdrawn_at", null);
  return { error: error ? new Error(error.message) : null };
}

// ─── Eligibility evaluation (used by the registration step) ────────────

export type StudentEligibilityProfile = {
  graduation_year: number | null;
  major: string | null;
  university: string | null;
  /** Profile doesn't track citizenship today, so this is always null and the
   * student can't be auto-blocked on us_citizen_required. The registration UI
   * surfaces the requirement as informational; the student self-attests
   * implicitly by checking the box. */
  us_citizen: boolean | null;
  gpa: number | null;
};

export type PositionEligibility =
  | { state: "eligible" }
  | { state: "ineligible"; reason: string }
  | { state: "unknown"; missingFields: string[] };

function normEq(a: string | null, b: string): boolean {
  return (a ?? "").trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Evaluate a student's profile against a position's filters. Returns:
 *   eligible — student passes everything
 *   ineligible — student concretely fails a filter (e.g., grad year out
 *     of range). The reason string is human-readable.
 *   unknown — at least one filter requires a profile field the student
 *     hasn't set. The student is shown as eligible-by-default with an
 *     inline link to fix their profile.
 */
export function evaluatePositionEligibility(
  position: Position,
  student: StudentEligibilityProfile
): PositionEligibility {
  const missing: string[] = [];

  // Graduation year
  if (position.grad_year_min != null || position.grad_year_max != null) {
    if (student.graduation_year == null) {
      missing.push("graduation_year");
    } else {
      const y = student.graduation_year;
      if (position.grad_year_min != null && y < position.grad_year_min) {
        return {
          state: "ineligible",
          reason:
            "Requires graduation year " +
            formatYearRange(position.grad_year_min, position.grad_year_max) +
            " — your profile shows " +
            y +
            ".",
        };
      }
      if (position.grad_year_max != null && y > position.grad_year_max) {
        return {
          state: "ineligible",
          reason:
            "Requires graduation year " +
            formatYearRange(position.grad_year_min, position.grad_year_max) +
            " — your profile shows " +
            y +
            ".",
        };
      }
    }
  }

  // Majors
  if (position.eligible_majors.length > 0) {
    if (!student.major) {
      missing.push("major");
    } else if (!position.eligible_majors.some((m) => normEq(student.major, m))) {
      return {
        state: "ineligible",
        reason:
          "Open to " +
          humanList(position.eligible_majors) +
          " — your major is " +
          student.major +
          ".",
      };
    }
  }

  // Universities
  if (position.eligible_universities.length > 0) {
    if (!student.university) {
      missing.push("university");
    } else if (
      !position.eligible_universities.some((u) => normEq(student.university, u))
    ) {
      return {
        state: "ineligible",
        reason:
          "Open to " +
          humanList(position.eligible_universities) +
          " — your school is " +
          student.university +
          ".",
      };
    }
  }

  // GPA — soft-evaluated. If the student hasn't entered a GPA, treat as
  // unknown (let them self-attest by opting in). If they have, gate hard.
  if (position.gpa_min != null) {
    if (student.gpa == null) {
      missing.push("gpa");
    } else if (student.gpa < position.gpa_min) {
      return {
        state: "ineligible",
        reason:
          "Requires GPA " +
          position.gpa_min.toFixed(1) +
          "+ — your profile shows " +
          student.gpa.toFixed(1) +
          ".",
      };
    }
  }

  // us_citizen_required is informational only — the student profile
  // doesn't track citizenship, so we never block. The position card
  // surfaces the requirement so the student knows they're attesting to it
  // by opting in.

  if (missing.length > 0) return { state: "unknown", missingFields: missing };
  return { state: "eligible" };
}

function formatYearRange(min: number | null, max: number | null): string {
  if (min != null && max != null) return min === max ? String(min) : min + "–" + max;
  if (min != null) return min + "+";
  if (max != null) return "up to " + max;
  return "any";
}

function humanList(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return items[0] + " and " + items[1];
  return items.slice(0, -1).join(", ") + ", and " + items[items.length - 1];
}
